import { useState } from 'react';
import { Flag } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

interface EhFlagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flaggedResourceId: string;
  flagData?: Record<string, unknown>;
  onClose: (flaggingSuccess: boolean) => void;
  textPreview?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
}

const DEFAULT_REASON = 'inappropriate';

export function EhFlagDialog({
  open,
  onOpenChange,
  flaggedResourceId,
  flagData,
  onClose,
  textPreview,
  thumbnailUrl,
  videoUrl,
}: EhFlagDialogProps) {
  const [reason, setReason] = useState<'inappropriate' | 'bad_quality' | ''>(DEFAULT_REASON);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      setError('Please select a reason.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('eh-content-flag', {
        method: 'POST',
        body: {
            flaggedResourceId,
            flagReason: reason,
            flagComment: comment,
            flagData,
        },
      });

      if (error || !data) {
        throw new Error(`Failed to submit flag request: ${error?.message}`);
      }

      setSuccess(true);
    } catch (err) {
      setError(import.meta.env.DEV && err instanceof Error
        ? err.message
        : 'Sorry, an error occurred. Please contact us by email at content@eveninghoney.ai.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose(success);
    onOpenChange(false);
    // Reset state for next time
    setTimeout(() => {
      setSuccess(false);
      setReason(DEFAULT_REASON);
      setComment('');
      setError('');
    }, 300);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white">
        {success ? (
          <>
            <DialogHeader>
              <DialogTitle>Report Submitted</DialogTitle>
              <DialogDescription>
                Thank you for your feedback.
                {/* Our team will review your report shortly. */}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" className="bg-zinc-800" onClick={handleClose}>Close</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 md:text-xl">
                <Flag className="size-5" />
                <span>Report Content</span>
              </DialogTitle>
              <DialogDescription className="text-md">
                Help us improve by reporting inappropriate AI-generated content.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2 max-w-full overflow-hidden">
              {textPreview && (
                <div className="flex justify-center">
                  <div className="text-sm text-zinc-400 italic text-ellipsis text-nowrap overflow-hidden mb-2 px-4">{textPreview}</div>
                </div>
              )}
              {thumbnailUrl && (
                <div className="flex justify-center">
                  <img src={thumbnailUrl} alt="Content thumbnail" className="rounded-lg max-h-40" />
                </div>
              )}
              {videoUrl && (
                <div className="flex justify-center">
                  <video src={videoUrl} className="rounded-lg max-h-60" controls />
                </div>
              )}

              <div className="space-y-4">
                <RadioGroup onValueChange={(value: 'inappropriate' | 'bad_quality') => setReason(value)} value={reason}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="inappropriate" id="r1" />
                    <Label htmlFor="r1">Inappropriate</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bad_quality" id="r2" />
                    <Label htmlFor="r2">Bad quality</Label>
                  </div>
                </RadioGroup>

                <Textarea
                  placeholder="Optional: Provide more details..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={500}
                  className="max-h-24 text-sm bg-zinc-800 border-zinc-700 resize-none"
                />
              </div>
              {error && <div className="text-red-500 text-sm text-center">{error}</div>}
            </div>


            <DialogFooter>
              <Button
                variant="ghost"
                className="bg-zinc-800"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-500"
                onClick={handleSubmit}
                disabled={isSubmitting || !reason}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 size-4 border-2 border-white/30 border-t-white rounded-full" />
                    <div>Submitting...</div>
                  </div>
                ) : 'Submit Report'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 